import { useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { getPascoService } from '@src/container';
import type { PascoDTO, UpdatePascoDTO } from '@src/application/pascos/dto';

/**
 * Hook: useUpdatePasco
 * Updates an existing PASCO record
 * 
 * @param options - React Query mutation options
 * @returns Mutation result for updating PASCO
 * 
 * @example
 * ```typescript
 * const { mutate: updatePasco, isPending, error } = useUpdatePasco({
 *     onSuccess: (updatedPasco) => {
 *         console.log('PASCO updated:', updatedPasco);
 *     }
 * });
 * 
 * // Usage
 * updatePasco({
 *     id: 'pasco-123',
 *     courseCode: 'CS101',
 *     courseName: 'Updated Course Name'
 * });
 * ```
 */
export function useUpdatePasco(
    options?: Omit<UseMutationOptions<PascoDTO, Error, UpdatePascoDTO>, 'mutationFn'>
) {
    const pascoService = getPascoService();
    const queryClient = useQueryClient();

    // Extract user's onSuccess before creating mutation
    const { onSuccess: userOnSuccess, ...restOptions } = options || {};

    return useMutation({
        ...restOptions,
        mutationFn: async (updateData: UpdatePascoDTO) => {
            return await pascoService.update(updateData);
        },
        onSuccess: async (data, variables, context, extra) => {
            // Invalidate and refetch pascos list
            await queryClient.invalidateQueries({
                queryKey: ['pascos'],
                predicate: (query) => query.queryKey.length === 1 // Only invalidate list queries
            });

            // Update the specific pasco in cache
            queryClient.setQueryData(['pascos', data.pascoId], data);

            // Call user's onSuccess callback if provided
            if (userOnSuccess) {
                await userOnSuccess(data, variables, context, extra);
            }
        },
    });
}
